import { Button } from '@hologram-dimension/button';
import { Icon } from '@hologram-dimension/icon';
import { Panel } from '@hologram-dimension/panel';
import { PanelNotification } from '@hologram-dimension/panel-notification';
import { ColorTokenNames } from '@hologram-dimension/tokens/colors';
import { useSimDetails, useSimFallbackHistory, useSimProfiles } from 'api';
import { EmptyState, Field, Table } from 'components';
import { useMemo } from 'react';
import { formatDateTime } from 'utils/dates';
import { HeaderItem, TableCellItem } from '../Table/types';
import styles from './SimFallbackHistory.module.css';

interface SimFallbackHistoryProps {
  hologramSimId?: HologramSimId;
}

const headers: Array<HeaderItem> = [
  { children: 'Event', key: 'Event' },
  { children: 'Date and time (UTC)', key: 'Date and time (UTC)' },
  { children: 'From profile ICCID', key: 'From profile ICCID' },
  { children: ' ', key: 'arrow' },
  { children: 'To profile ICCID', key: 'To profile ICCID' },
  { children: ' ', key: 'filler' },
];

const SimFallbackHistory: React.FC<SimFallbackHistoryProps> = ({ hologramSimId }) => {
  const {
    simDetails,
    error: simDetailsError,
    isLoading: simDetailsIsLoading,
  } = useSimDetails(hologramSimId);
  const {
    simProfiles,
    error: simProfilesError,
    isLoading: simProfilesIsLoading,
  } = useSimProfiles(hologramSimId);
  const {
    fallbackHistory,
    isLoading: fallbackHistoryIsLoading,
    error: fallbackHistoryError,
  } = useSimFallbackHistory(hologramSimId);

  const colors: Record<Iccid, ColorTokenNames> = useMemo(() => {
    const newColors: Record<Iccid, ColorTokenNames> = {};
    const possibleTokens: Array<ColorTokenNames> = [
      'DdsColorPaletteTeal20',
      'DdsColorPaletteTeal60',
      'DdsColorPaletteTeal30',
      'DdsColorPaletteTeal40',
    ];
    simProfiles.forEach((profile) => {
      if (profile.iccid) {
        newColors[profile.iccid] = possibleTokens.pop() ?? 'DdsColorPaletteTeal40';
      }
    });
    return newColors;
  }, [simProfiles]);

  const error = simDetailsError || fallbackHistoryError || simProfilesError;
  const isLoading = simDetailsIsLoading || fallbackHistoryIsLoading || simProfilesIsLoading;

  // TODO: Respect the release flag
  const fallback = !!simDetails.fallbackInstalled;

  // TODO: remove the length check here when fallback flag is more reliable
  // TODO: Maybe don't load fallback history at all if fallback is disabled.
  if (
    (!fallback && fallbackHistory.length === 0) ||
    (fallback && fallbackHistory.length === 0 && simProfiles.length === 1)
  ) {
    return null;
  }

  let panelContents;
  if (error) {
    panelContents = (
      <PanelNotification
        variant="error"
        label="We ran into an issue loading your SIM fallback history"
        actions={
          <Button
            variant="secondary"
            iconStart="Sync"
            onClick={() => {
              window.location.reload();
            }}
          >
            Reload page
          </Button>
        }
      >
        We will keep trying in the background or you can try to reload the page.
      </PanelNotification>
    );
  } else if (isLoading) {
    panelContents = <PanelNotification>Loading...</PanelNotification>;
  } else if (fallbackHistory.length === 0) {
    panelContents = (
      <PanelNotification icon="SwapSIM">
        This SIM has not used network fallback protection yet.
      </PanelNotification>
    );
  } else {
    const rows = fallbackHistory.map((event, index) => {
      const { fallbackTime, fromIccid, fromProfileName, toIccid, toProfileName } = event;

      const cells: Array<TableCellItem> = new Array(5);

      cells[0] = {
        key: toProfileName ?? index.toString(),
        children: (
          <div className={styles.eventCell}>
            <Icon name="SwapSIM" fill={toIccid ? colors[toIccid] : 'DdsColorPaletteTeal40'} />
            <span>Switched to {toProfileName}</span>
          </div>
        ),
      };
      cells[1] = {
        key: fallbackTime ?? '',
        className: styles.timeCell,
        children: formatDateTime(fallbackTime, 'table'),
      };
      cells[2] = {
        key: fromIccid ?? '',
        className: styles.iccidCell,
        children: (
          <Field
            className={styles.field}
            direction="column"
            labelClassName={styles.iccid}
            valueClassname={styles.profile}
            label={fromIccid ?? ''}
            value={fromProfileName ?? <EmptyState />}
          />
        ),
      };
      cells[3] = {
        key: 'arrow-3',
        className: styles.arrowCell,
        children: <Icon size="small" name="ArrowEastLong" fill="DdsColorPaletteGrey60" />, // TODO: @DIMENSIONV2 UPDATE
      };
      cells[4] = {
        key: toIccid ?? '',
        className: styles.iccidCell,
        children: (
          <Field
            className={styles.field}
            direction="column"
            labelClassName={styles.iccid}
            valueClassname={styles.profile}
            label={toIccid ?? ''}
            value={toProfileName ?? <EmptyState />}
          />
        ),
      };
      cells[5] = {
        key: 'filler',
        children: ' ',
      };

      return { key: `${fallbackTime}-${fromIccid}-${toIccid}`, cells };
    });

    panelContents = <Table headers={headers} rows={rows} />;
  }

  return (
    <Panel header="Fallback history" noContentPadding isLoading={isLoading}>
      {panelContents}
    </Panel>
  );
};

export default SimFallbackHistory;
